import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceArea,
    ReferenceLine,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useMemo, useCallback } from "react";
import { FIELD_NAME_TO_TRANSLATION_LABEL, LineChartProps, ValveSegment } from "./LineChartCommon";

const VALVE_POSITION_COLORS = {
    FULL_SOLAR: "#72A363",
    HYBRID: "#FFD700",
    WATER_HEATER: "#FFA500",
    HEATER: "#FF6B6B",
} as const;

const TemperatureLineChart = ({ lineData, currentDevice, ticks, lineChartXTickFormatter, isTelemetryFieldVisible }: LineChartProps) => {
    
    const { t } = useTranslation("dashboard")

    const showCirculator = isTelemetryFieldVisible?.("circulator_state") ?? true;
    const showValve = isTelemetryFieldVisible?.("valve_position") ?? true;
    
    const uniqueValvePositions = useMemo(() => {
        if (!showValve || !lineData || lineData.length === 0) return [];
        const positions = new Set<string>();
        lineData.forEach((point: any) => {
            if (point.valve_position) {
                positions.add(point.valve_position);
            }
        });
        return Array.from(positions);
    }, [lineData, showValve]);
    
    
    const configNumber = useMemo(() => {
        if (!currentDevice?.parameter_configuration?.name) return null;
        const match = currentDevice.parameter_configuration.name.match(/Config\s+(\d+)/i);
        return match ? parseInt(match[1]) : null;
    }, [currentDevice?.parameter_configuration?.name]);
    
    const getValvePositionColorForConfig = useCallback((normalized: string, config: number): string | null => {
        if (config === 2) {
            if (normalized === "FULL_SOLAR") {
                return VALVE_POSITION_COLORS.FULL_SOLAR;
            }
            if (normalized === "HYBRID") {
                return VALVE_POSITION_COLORS.HYBRID;
            }
        }
        
        if (config === 8) {
            if (normalized === "WATER_HEATER") {
                return VALVE_POSITION_COLORS.WATER_HEATER;
            }
            if (normalized === "HEATER") {
                return VALVE_POSITION_COLORS.HEATER;
            }
        }
        
        return null;
    }, []);
    
    const getValvePositionColorFallback = useCallback((normalized: string): string | null => {
        if (normalized === "WATER_HEATER") {
            return VALVE_POSITION_COLORS.WATER_HEATER;
        }
        if (normalized === "HEATER") {
            return VALVE_POSITION_COLORS.HEATER;
        }
        if (normalized === "FULL_SOLAR") {
            return VALVE_POSITION_COLORS.FULL_SOLAR;
        }
        if (normalized === "HYBRID") {
            return VALVE_POSITION_COLORS.HYBRID;
        }
        return null;
    }, []);
    
    const getValvePositionColor = useCallback((valvePosition: string | null | undefined): string | null => {
        if (!valvePosition) return null;
        const normalized = valvePosition.toUpperCase();
        
        if (configNumber !== null) {
            const configColor = getValvePositionColorForConfig(normalized, configNumber);
            if (configColor) return configColor;
        }
        
        return getValvePositionColorFallback(normalized);
    }, [configNumber, getValvePositionColorForConfig, getValvePositionColorFallback]);

    const VALVE_LINE_Y = 5;
    
    const groupValvePositionSegments = useCallback((data: any[]): ValveSegment[] => {
        const segments: ValveSegment[] = [];
        type SegmentType = { position: string; color: string; startIndex: number };
        let currentSeg: SegmentType | null = null;
        
        data.forEach((point, index) => {
            const position = point.valve_position;
            const color = getValvePositionColor(position);
            
            if (!color) return;
            
            if (!currentSeg || currentSeg.position !== position) {
                if (currentSeg !== null) {
                    segments.push({
                        position: currentSeg.position,
                        color: currentSeg.color,
                        dataKey: `valve_line_${segments.length}`,
                        startIndex: currentSeg.startIndex,
                        endIndex: index - 1
                    });
                }
                currentSeg = {
                    position: position || "",
                    color: color,
                    startIndex: index
                };
            }
        });
        
        if (currentSeg !== null) {
            const seg = currentSeg as SegmentType;
            segments.push({
                position: seg.position,
                color: seg.color,
                dataKey: `valve_line_${segments.length}`,
                startIndex: seg.startIndex,
                endIndex: data.length - 1
            });
        }
        
        return segments;
    }, [getValvePositionColor]);
    
    const extendSinglePointSegments = useCallback((segments: ValveSegment[]): ValveSegment[] => {
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            if (segment.startIndex === segment.endIndex && i < segments.length - 1) {
                const nextSegment = segments[i + 1];
                if (nextSegment.startIndex === segment.endIndex + 1) {
                    segment.endIndex = nextSegment.startIndex;
                }
            }
        }
        return segments;
    }, []);
    
    const addValveLineValuesToData = useCallback((
        data: any[], 
        segments: ValveSegment[]
    ): any[] => {
        return data.map((point, index) => {
            const newPoint: any = { ...point };
            
            segments.forEach((segment) => {
                newPoint[segment.dataKey] = null;
            });
            
            segments.forEach((segment) => {
                if (index >= segment.startIndex && index <= segment.endIndex) {
                    newPoint[segment.dataKey] = VALVE_LINE_Y;
                }
            });
            
            for (let segIndex = 0; segIndex < segments.length - 1; segIndex++) {
                const currentSegment = segments[segIndex];
                const nextSegment = segments[segIndex + 1];
                
                const isTransitionPoint = 
                    (index === currentSegment.endIndex && nextSegment.startIndex === index + 1) ||
                    (index === nextSegment.startIndex && currentSegment.endIndex === index - 1);
                
                if (isTransitionPoint) {
                    newPoint[currentSegment.dataKey] = VALVE_LINE_Y;
                    newPoint[nextSegment.dataKey] = VALVE_LINE_Y;
                }
            }
            
            return newPoint;
        });
    }, []);
    
    const valvePositionLineSegments = useMemo(() => {
        if (!showValve || !lineData || lineData.length === 0) {
            return { segments: [], data: lineData, uniquePositions: [] };
        }
        
        const segments = groupValvePositionSegments(lineData);
        const extendedSegments = extendSinglePointSegments(segments);
        const finalData = addValveLineValuesToData(lineData, extendedSegments);
        
        const uniquePositions = Array.from(new Set(extendedSegments.map(s => s.position)));
        
        return { segments: extendedSegments, data: finalData, uniquePositions };
    }, [lineData, showValve, groupValvePositionSegments, extendSinglePointSegments, addValveLineValuesToData]);

    const standbyIntervals = useMemo(() => {
        if (!lineData?.length) return [] as { start: number; end: number }[];
        const intervals: { start: number; end: number }[] = [];
        let start: number | null = null;
        let lastOnTime: number | null = null;

        lineData.forEach((point: any) => {
            const on = point.schedule_standby_mask != null && point.schedule_standby_mask > 0;
            if (on) {
                if (start == null) start = point.time;
                lastOnTime = point.time;
            } else if (start != null && lastOnTime != null) {
                intervals.push({ start, end: point.time });
                start = null;
                lastOnTime = null;
            }
        });
        if (start != null && lastOnTime != null) {
            intervals.push({ start, end: lastOnTime });
        }
        return intervals;
    }, [lineData]);

    const chartDataWithStandbyLine = useMemo(() => {
        const source = valvePositionLineSegments.data || lineData;
        if (!source?.length) return source;

        return source.map((point: any) => {
            const inBand = standbyIntervals.some(
                (interval) => point.time >= interval.start && point.time <= interval.end
            );
            return {
                ...point,
                schedule_standby_line: inBand ? 1 : null,
            };
        });
    }, [lineData, valvePositionLineSegments.data, standbyIntervals]);

    
    const renderLegendItem = useCallback((
        displayName: string,
        strokeColor: string,
        strokeWidth: number = 4,
        key: string
    ) => {
        return (
            <li 
                key={key}
                className="recharts-legend-item" 
                style={{ display: 'inline-block', marginRight: '10px' }}
            >
                <svg 
                    className="recharts-surface" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 32 32" 
                    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}
                >
                    <title></title>
                    <desc></desc>
                    <path 
                        strokeWidth={strokeWidth} 
                        fill="none" 
                        stroke={strokeColor} 
                        d="M0,16h10.666666666666666
                            A5.333333333333333,5.333333333333333,0,1,1,21.333333333333332,16
                            H32M21.333333333333332,16
                            A5.333333333333333,5.333333333333333,0,1,1,10.666666666666666,16"
                        className="recharts-legend-icon"
                    />
                </svg>
                <span 
                    className="recharts-legend-item-text" 
                    style={{ color: strokeColor }}
                >
                    {displayName}
                </span>
            </li>
        );
    }, []);

    const renderCustomizedTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const pt = lineData.find((elem: any) => elem.time === label);
            if (pt) {
                const filteredPayload = payload.filter((line: any) => 
                    line.dataKey !== "circulator_line" && 
                    line.dataKey !== "schedule_standby_mask" &&
                    line.dataKey !== "schedule_standby_line" &&
                    !line.dataKey?.startsWith("valve_line_")
                );
                
                const valvePositionColor = showValve && pt.valve_position ? getValvePositionColor(pt.valve_position) : null;
                
                return (
                <div className="tooltip-container">
                    <p style={{ fontWeight: 700 }}>{lineChartXTickFormatter(parseInt(pt.time))}</p>
                    {filteredPayload.map((line: any, index: number) => (
                    <p key={index} style={{ color: line.stroke, fontWeight: 500 }}>
                        {t(line.name, { device_name: currentDevice?.name })}:{" "}
                        {pt[line.dataKey]} °C
                    </p>
                    ))}
                    {pt.schedule_standby_mask > 0 && (
                        <p style={{ color: "#6b7280", fontWeight: 500, marginTop: "4px" }}>
                            {t("schedule_standby")}: ON
                        </p>
                    )}
                    {showCirculator && pt.circulator_state && (
                        <p style={{ color: "#4169E1", fontWeight: 500, marginTop: "4px" }}>
                            {t("last_info_circulator_state")}: {pt.circulator_state}
                        </p>
                    )}
                    {showValve && pt.valve_position && (
                        <p style={{ color: valvePositionColor || "#000", fontWeight: 500, marginTop: "4px" }}>
                            {t("last_info_valve_position")}: {pt.valve_position.replace(/_/g, " ")}
                        </p>
                    )}
                </div>
                );
            }
        }
        return <></>;
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                style={{ marginLeft: "-10px" }}
                width={500}
                height={300}
                data={chartDataWithStandbyLine}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey='time'
                    name='Time'
                    scale='time'
                    type='number'
                    domain={['dataMin', 'dataMax']} 
                    tickFormatter={ lineChartXTickFormatter }
                    ticks={ticks}
                />
                <YAxis
                    yAxisId="temp"
                    unit={"°C"}
                      domain={[
                        (dataMin: number) => 0,
                        (dataMax: number) => (dataMax * 1.15).toFixed(0),
                      ]}
                />
                {/* Full chart height for standby bands (not °C scale) */}
                <YAxis yAxisId="standby" domain={[0, 1]} hide />
                <Tooltip content={renderCustomizedTooltip} />
                <Legend
                    content={({ payload }: any) => {
                        if (!payload || !payload.length) return null;
                        
                        const standardEntries = payload.filter((entry: any) => 
                            !entry.dataKey?.startsWith("valve_line_") &&
                            (showCirculator || entry.dataKey !== "circulator_line")
                        );
                        
                        const valvePositionEntries = showValve
                            ? uniqueValvePositions.map((position: string) => {
                            const color = getValvePositionColor(position);
                            if (!color) return null;
                            
                            return {
                                value: `${t("last_info_valve_position")}: ${position.replace(/_/g, " ")}`,
                                color: color,
                            };
                        }).filter(Boolean)
                            : [];
                        
                        return (
                            <ul className="recharts-default-legend" style={{ padding: 0, margin: 0, textAlign: 'center' }}>
                                {standardEntries.map((entry: any, index: number) => {
                                    const displayName = t(FIELD_NAME_TO_TRANSLATION_LABEL[entry.dataKey] || entry.dataKey, {
                                        device_name: currentDevice?.name,
                                    });
                                    const strokeColor = entry.color || entry.stroke;
                                    const strokeWidth = entry.strokeWidth || 4;
                                    
                                    return renderLegendItem(
                                        displayName,
                                        strokeColor,
                                        strokeWidth,
                                        `legend-item-${index}`
                                    );
                                })}
                                {valvePositionEntries.map((entry: any, index: number) => 
                                    renderLegendItem(
                                        entry.value,
                                        entry.color,
                                        4,
                                        `valve-legend-item-${index}`
                                    )
                                )}
                            </ul>
                        );
                    }}
                />
                {standbyIntervals.map((interval) => (
                    <ReferenceArea
                        key={`standby-band-${interval.start}-${interval.end}`}
                        yAxisId="standby"
                        x1={interval.start}
                        x2={interval.end === interval.start ? interval.start + 1 : interval.end}
                        y1={0}
                        y2={1}
                        fill="#6b7280"
                        fillOpacity={0.25}
                        stroke="none"
                        ifOverflow="extendDomain"
                    />
                ))}
                {standbyIntervals.map((interval) => (
                    <ReferenceLine
                        key={`standby-start-${interval.start}`}
                        yAxisId="standby"
                        x={interval.start}
                        stroke="#6b7280"
                        strokeWidth={2}
                    />
                ))}
                {standbyIntervals.map((interval) =>
                    interval.end !== interval.start ? (
                        <ReferenceLine
                            key={`standby-end-${interval.end}`}
                            yAxisId="standby"
                            x={interval.end}
                            stroke="#6b7280"
                            strokeWidth={2}
                        />
                    ) : null
                )}
                <Line
                    yAxisId="temp"
                    type="monotone"
                    dataKey="water_temperature"
                    stroke="#F7AA3F"
                    activeDot={{ r: 4 }}
                    dot={false}
                />
                {currentDevice?.mode !== "DUO" && (<Line
                        yAxisId="temp"
                        type="monotone"
                        dataKey="min_planning_temp"
                        stroke="#012124"
                        activeDot={ { r: 4 } }
                        dot={ false }
                    />
                )}
                <Line
                    yAxisId="standby"
                    type="stepAfter"
                    dataKey="schedule_standby_line"
                    stroke="#6b7280"
                    strokeWidth={2}
                    activeDot={{ r: 3 }}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                />
                {showCirculator && (
                <Line
                    yAxisId="temp"
                    type="stepAfter"
                    dataKey="circulator_line"
                    name="circulator_state"
                    stroke="#4169E1"
                    activeDot={{ r: 4 }}
                    dot={false}
                    connectNulls={true}
                />
                )}
                {showValve && valvePositionLineSegments.segments && valvePositionLineSegments.segments.length > 0 && valvePositionLineSegments.segments.map((segment: { dataKey: string; color: string; position: string; startIndex: number; endIndex: number }, index: number) => (
                    <Line
                        yAxisId="temp"
                        key={`valve-line-${index}`}
                        type="monotone"
                        dataKey={segment.dataKey}
                        name={`valve_position_${segment.position}`}
                        stroke={segment.color}
                        strokeWidth={4}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        connectNulls={false}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    )
}

export default TemperatureLineChart
